import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { OnSelectMultiple } from 'libs/end-to-end/src/ui-types';
import { OnSelectSingle } from 'libs/end-to-end/src/ui-types';
import { z } from 'zod';
import { useSessionStorage } from 'apps/sigma2-ui/src/lib/hooks/useSessionStorage';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';

type MetricSelectProps = (
  | UseDynamicMetricsMultiPayload
  | Omit<UseDynamicMetricsSinglePayload, 'preferredMetric'>
) & {
  widthClass?: string;
  backgroundColor?: string;
};

export const MetricSelect: React.FC<MetricSelectProps> = (payload) => {
  const language = useLanguage();

  const { metrics, type, isProcessing, widthClass } = payload;

  const isDisabled = useMemo(() => {
    if (!metrics) {
      return true;
    }

    return isProcessing !== undefined && isProcessing;
  }, [isProcessing, metrics]);

  return (
    <>
      {!!metrics && type === 'single' && (
        <SelectBoxWithSearch
          type={type}
          list={metrics.map(({ columnHeaderValue, displayName }) => {
            return {
              optionLabel: displayName,
              optionValue: columnHeaderValue,
            };
          })}
          widthClass={widthClass}
          label={getTranslatedContentAsString(language.metricBarSelectLabel())}
          selected={payload.selectedMetric ?? ''}
          setSelected={payload.onSelectSingle}
          disabled={isDisabled}
        />
      )}

      {!!metrics && type === 'multi' && (
        <SelectBoxWithSearch
          type={type}
          list={metrics.map(({ columnHeaderValue, displayName }) => {
            return {
              optionLabel: displayName,
              optionValue: columnHeaderValue,
            };
          })}
          label={getTranslatedContentAsString(language.metricBarSelectLabel())}
          selected={payload.selectedMetrics}
          setSelected={payload.onSelectMultiple}
          widthClass={widthClass}
          disabled={isDisabled}
        />
      )}
    </>
  );
};

export type UseDynamicMetricsParams =
  | UseDynamicMetricsSingleParams
  | UseDynamicMetricsMultiParams;

export interface UseDynamicMetricsSingleParams {
  type: 'single';
  focusArea: FocusAreaDefinition['focusArea'];
  automaticallySelectFirstPreferred: boolean;
  persistKey?: string;
}

export interface UseDynamicMetricsMultiParams {
  type: 'multi';
  focusArea: FocusAreaDefinition['focusArea'];
  automaticallySelectAll?: boolean;
  persistKey?: string;
}

export type UseDynamicMetricsPayload =
  | UseDynamicMetricsSinglePayload
  | UseDynamicMetricsMultiPayload;

export interface UseDynamicMetricsSinglePayload {
  type: 'single';
  metrics: MetricDefinition[] | null;
  isProcessing?: boolean;
  /**
   * columnHeaderValue
   */
  selectedMetric: string | null;
  /**
   * columnHeaderValue
   */

  preferredMetric: MyMetricDefinition | null;
  onSelectSingle: OnSelectSingle;
  resetMetricsToInitialState: () => void;
}

export interface UseDynamicMetricsMultiPayload {
  type: 'multi';
  metrics: MetricDefinition[] | null;
  isProcessing?: boolean;
  /**
   * columnHeaderValues
   */
  selectedMetrics: string[];
  /**
   * columnHeaderValues
   */
  onSelectMultiple: OnSelectMultiple;
  resetMetricsToInitialState: () => void;
}

export type UseDynamicMetricReturnType<T extends UseDynamicMetricsParams> =
  T extends UseDynamicMetricsSingleParams
    ? UseDynamicMetricsSinglePayload
    : UseDynamicMetricsMultiPayload;

const DynamicMetricsLocalStorageSchema = z.object({
  selectedMetrics: z.array(z.string()).nullable(),
});

type DynamicMetricsLocalStorageSchema = z.infer<
  typeof DynamicMetricsLocalStorageSchema
>;

// eslint-disable-next-line complexity
const getSelectedMetrics = <T extends UseDynamicMetricsParams>({
  metricsForFocusArea,
  value,
  params,
  persistKey,
  preferredMetric,
}: {
  myObservation: Nullable<MyObservation>;
  metricsForFocusArea: MetricDefinition[];
  value: DynamicMetricsLocalStorageSchema;
  params: T;
  persistKey?: string;
  preferredMetric: MetricDefinition;
}): Nullable<string[]> => {
  if (params.type === 'single') {
    if (
      persistKey &&
      value.selectedMetrics &&
      value.selectedMetrics.length > 0 &&
      value.selectedMetrics.every((x) =>
        metricsForFocusArea.some((y) => y.columnHeaderValue === x),
      )
    ) {
      return value.selectedMetrics;
    }
    if (
      params.automaticallySelectFirstPreferred &&
      metricsForFocusArea.length > 0
    ) {
      return [preferredMetric.columnHeaderValue];
    }
  } else if (params.type === 'multi') {
    if (value.selectedMetrics) {
      return value.selectedMetrics;
    }
    if (params.automaticallySelectAll) {
      return metricsForFocusArea.map((x) => x.columnHeaderValue);
    }
  } else {
    throw guardExhaustive(params);
  }

  // For TS, this should never happen
  return null;
};

export const useDynamicMetrics = <T extends UseDynamicMetricsParams>(
  params: T,
): UseDynamicMetricReturnType<T> => {
  const { myObservation } = useObservation();

  const { type, focusArea, persistKey } = useMemo(() => {
    return params;
  }, [params]);

  /**
   * columnHeaderValues
   */
  const [selectedMetrics, internalSetSelectedMetrics] = useState<string[]>([]);

  const [value, setValue] = useSessionStorage({
    key: params.persistKey ?? 'useDynamicMetrics',
    schema: DynamicMetricsLocalStorageSchema,
    defaultValue: { selectedMetrics: null },
  });

  const setSelectedMetrics = useCallback(
    (value: string[]) => {
      internalSetSelectedMetrics(value);
      setValue(() => {
        return {
          selectedMetrics: [...value],
        };
      });
    },
    [setValue],
  );

  const [didAutoLoad, setDidAutoLoad] = useState(false);

  const onSelectSingle: OnSelectSingle = useCallback(
    (value) => {
      if (typeof value === 'function') {
        return;
      }
      setSelectedMetrics(value ? [value] : []);
    },
    [setSelectedMetrics],
  );

  const onSelectMultiple: OnSelectMultiple = useCallback(
    (values) => {
      if (typeof values === 'function') {
        return;
      }
      setSelectedMetrics(values);
    },
    [setSelectedMetrics],
  );

  const metricsForFocusArea = useMemo(() => {
    return (
      myObservation?.focusAreas.find((x) => x.focusArea === focusArea)
        ?.metrics ?? []
    );
  }, [focusArea, myObservation]);

  const preferredMetric = useMemo(() => {
    return (
      metricsForFocusArea.find((x) => x.isPreferred) ??
      metricsForFocusArea[0] ??
      null
    );
  }, [metricsForFocusArea]);

  const setMetricsToInitialState = useCallback(() => {
    const selectedMetrics = getSelectedMetrics({
      preferredMetric,
      myObservation,
      metricsForFocusArea,
      value,
      params,
      persistKey,
    });

    setSelectedMetrics(selectedMetrics ?? []);
  }, [
    setSelectedMetrics,
    metricsForFocusArea,
    myObservation,
    params,
    persistKey,
    preferredMetric,
    value,
  ]);

  useEffect(() => {
    if (didAutoLoad) {
      return;
    }

    if (!myObservation || !metricsForFocusArea) {
      return;
    }

    setMetricsToInitialState();
    setDidAutoLoad(true);
  }, [
    setMetricsToInitialState,
    didAutoLoad,
    metricsForFocusArea,
    myObservation,
  ]);

  const metrics = useMemo(() => {
    const metricsForFocusArea = myObservation?.focusAreas.find(
      (x) => x.focusArea === focusArea,
    )?.metrics;

    if (!metricsForFocusArea) {
      return null;
    }

    return metricsForFocusArea;
  }, [myObservation, focusArea]);

  return useMemo(() => {
    if (type === 'single') {
      return {
        type,
        metrics,
        onSelectSingle,
        selectedMetric: selectedMetrics[0] ?? null,
        resetMetricsToInitialState: setMetricsToInitialState,
        preferredMetric: preferredMetric,
      } as UseDynamicMetricReturnType<T>;
    } else if (type === 'multi') {
      return {
        type,
        metrics,
        onSelectMultiple: onSelectMultiple,
        selectedMetrics,
        resetMetricsToInitialState: setMetricsToInitialState,
      } as UseDynamicMetricReturnType<T>;
    } else {
      throw guardExhaustive(type);
    }
  }, [
    type,
    metrics,
    onSelectSingle,
    selectedMetrics,
    setMetricsToInitialState,
    preferredMetric,
    onSelectMultiple,
  ]);
};
